import {
  type BlockInteractionsBlockBy,
  type BlockInteractionsBlockingTypes,
  type BlockInteractionsRule,
  type Modification,
  type NavigationEditSchemaType,
  RuleApplyOnType,
} from '@carboncopyco/types';
import { Button } from '@carboncopyco/ui';
import { useMemo, useState } from 'react';
import { ApplyOnDropdown } from '../ApplyOnDropdown';
import { FindByDropDown } from './FindByDropDown';
import { getErrorLabel } from './utils';

export type onSubmitBlockInteractionProps = {
  blockAcross: RuleApplyOnType;
  blockBy: BlockInteractionsBlockBy;
};

export type BlockInteractionFormProps = {
  elementMetadata: Partial<HTMLButtonElement & HTMLAnchorElement>;
  linkEditRule?: Modification<NavigationEditSchemaType.blockInteractions>;
  elementType: BlockInteractionsBlockingTypes;
  currentPageUrl: string;
  onSubmit: (opt: onSubmitBlockInteractionProps) => void;
  onCancel: () => void;
  getCurrentBlockingState?: (
    blockBy: BlockInteractionsBlockBy
  ) => BlockInteractionsBlockBy;
};

export const BlockInteractionForm = ({
  linkEditRule,
  elementType,
  elementMetadata,
  currentPageUrl,
  onSubmit,
  onCancel,
  getCurrentBlockingState,
}: BlockInteractionFormProps) => {
  const isEditMode = !!linkEditRule;
  const content = linkEditRule?.content as BlockInteractionsRule;
  const findByValue = (content as BlockInteractionsRule)?.blockBy;
  const [applyOn, setApplyOn] = useState<RuleApplyOnType>(
    isEditMode ? content.applyOn : RuleApplyOnType.allPages
  );
  const [findBy, setFindBy] = useState<BlockInteractionsBlockBy>(
    isEditMode ? findByValue : elementType === 'button' ? 'title' : 'url'
  );
  const isAlreadyBlocked = getCurrentBlockingState?.(findBy);

  const hasEditErrorMessage = useMemo(
    () => !!isAlreadyBlocked && getErrorLabel(elementType, isAlreadyBlocked),
    [isAlreadyBlocked, elementType]
  );

  const shouldDisableSubmit = () => {
    return !hasChangeMade() || !!hasEditErrorMessage;
  };

  const hasChangeMade = () => {
    if (!isEditMode) return true;

    return findByValue !== findBy || content.applyOn !== applyOn;
  };
  return (
    <div
      className="flex flex-col gap-4"
      aria-label="blockInteraction"
    >
      <FindByDropDown
        findBy={findBy}
        setFindBy={setFindBy}
        elementMetadata={elementMetadata}
        elementType={elementType}
        error={hasEditErrorMessage}
        initialOldValue={linkEditRule?.content.blockingValue}
      />
      <ApplyOnDropdown
        applyOn={applyOn}
        setApplyOn={setApplyOn}
        currentPageUrl={currentPageUrl}
      />
      <div className="flex justify-end">
        <div className="flex flex-row gap-2.5 mt-5">
          <Button
            label="Cancel"
            onClick={onCancel}
            highContrast
          />
          <Button
            disabled={shouldDisableSubmit()}
            aria-label="Submit"
            label={isEditMode ? 'Save' : 'Add rule'}
            onClick={() =>
              onSubmit({
                blockAcross: applyOn,
                blockBy: findBy,
              })
            }
          />
        </div>
      </div>
    </div>
  );
};
